// A DEPLOYMENT: field work as its own record, edited in place (Jeremy, 21 Oct 2026; migration
// 20261021T1900). Not a request. Its rules are here, in one place, so the API and the screens
// cannot disagree about who may do what.
//
// Who (Jeremy, 27 Oct 2026, from Michelle Kinsinger's problem reports: "No option for PM. Get rid
// of approval."):
//   - anyone may create one, and it is BOOKED when saved. Nobody confirms it;
//   - it has a PM, CHOSEN on the form (pre-filled with the Ajera job's PM, changeable, optional)
//     and stored by EE Key. The chosen PM, not the job's, holds the rights below;
//   - the creator, the chosen PM and HR may edit it and cancel it (cancelling needs a reason);
//   - those three and the crew may add notes.
// Who READS the notes is the private-details rule (visibility.ts; Jeremy, 22 Oct 2026), with the
// chosen PM as its "approver", so it holds everyone who may add one.
// Until 27 Oct a deployment was TENTATIVE until the job's PM or HR confirmed it [retired-claim].
import { blackoutMessage, blackoutOver } from './company-events.js';
import { conflicts } from './conflicts.js';
import { isValidISODate } from './dates.js';
import { requestLabel, requestRef } from './timeoff.js';
/**
 * A deployment is live (CONFIRMED, which every screen calls "Booked") or CANCELLED. The database
 * enum still holds TENTATIVE, because Postgres cannot drop an enum value, but nothing writes it
 * since 27 Oct 2026 and a CHECK refuses it (migration 20261027T1200). CONFIRMED is kept as the
 * live value's NAME so Fieldpath, which reads it, sees a live deployment exactly as before.
 */
export const DEPLOYMENT_STATUSES = ['CONFIRMED', 'CANCELLED'];
/** What a person reads for each status. Never "Confirmed", "Tentative" or "Approved" (27 Oct 2026). */
export const DEPLOYMENT_STATUS_LABEL = { CONFIRMED: 'Booked', CANCELLED: 'Cancelled' };
/**
 * How a deployment counts for the overlap gate and the calendar, which speak in request
 * statuses: a live one holds its crew as an approved request does, a cancelled one not at all.
 */
export function asRequestStatus(status) {
    return status === 'CONFIRMED' ? 'APPROVED' : 'CANCELLED';
}
export function deploymentRights(d, actor) {
    const pm = d.pmId !== null && d.pmId === actor.id;
    const creator = d.creatorId === actor.id;
    const owner = pm || creator || actor.isHr;
    const live = d.status !== 'CANCELLED';
    return {
        edit: live && owner,
        cancel: live && owner,
        note: live && (owner || d.crewIds.includes(actor.id)),
        as: pm ? 'PM' : creator ? 'CREATOR' : actor.isHr ? 'HR' : null,
    };
}
/**
 * The input and overlap rules for a deployment's dates and crew, as a sentence for the person,
 * or null when they are met. The same overlap rule the request gates apply (gates.ts, gate 2):
 * nobody on the crew may already be committed -- PTO, NTD or another deployment, pending or
 * approved -- on any day of it. `selfId` is this deployment's own id when editing, so it does
 * not conflict with itself.
 *
 * A company BLACKOUT on any day of it refuses it whole, before the overlap rule, as gate 1b does
 * for a request (S25, 1 Nov 2026). The API runs this on create and whenever the dates or the
 * crew change, so a deployment booked before the blackout existed keeps its dates and crew.
 */
export function checkDeployment(d, people, commitments, companyEvents, selfId = null) {
    const refuse = (message) => ({ message, conflicts: [] });
    if (!isValidISODate(d.start) || !isValidISODate(d.end))
        return refuse('Dates must be real YYYY-MM-DD dates.');
    if (d.end < d.start)
        return refuse('The last day has to fall on or after the first.');
    if (d.crewIds.length === 0)
        return refuse('Put at least one person on the crew.');
    if (new Set(d.crewIds).size !== d.crewIds.length)
        return refuse('Somebody is on the crew more than once.');
    for (const id of d.crewIds) {
        const p = people.get(id);
        if (!p)
            return refuse(`Unknown person ${id}.`);
        if (!p.active)
            return refuse(`${p.name} is not active in Ajera and cannot be booked.`);
    }
    const blackout = blackoutOver(d.start, d.end, companyEvents);
    if (blackout)
        return { message: blackoutMessage(blackout), conflicts: [], blackout };
    const exclude = new Set(selfId ? [selfId] : []);
    const hits = [];
    const lines = [];
    for (const id of d.crewIds) {
        const c = conflicts(id, d.start, d.end, commitments, exclude);
        for (const r of c)
            hits.push({ resourceId: id, requestId: r.id });
        if (c.length) {
            const name = people.get(id).name;
            lines.push(`${name} is already committed: ${c.map(r => `${requestRef(requestLabel(r.type, r.timeOffKind, false), r.start, r.end)} (${r.status.toLowerCase()})`).join('; ')}.`);
        }
    }
    return hits.length ? { message: `${lines.join(' ')} Change the dates or the crew, or cancel that first.`, conflicts: hits } : null;
}
//# sourceMappingURL=deployment.js.map